import React, {Component} from 'react';
import {Company, User} from '@prisma/client';
import {
  Autocomplete,
  Box,
  Fade,
  FormGroup,
  FormLabel,
  IconButton,
  Modal,
  Paper,
  TextField,
  Typography,
  Unstable_Grid2 as Grid,
  Alert, Snackbar,
} from '@mui/material';
import {Close, SaveAs} from '@mui/icons-material';
import './CreateUserModal.scss';
import {LoadingButton} from '@mui/lab';
import {createUser} from 'services/knestKontrol';

type State = {
  newUser: Partial<User>
  loading: boolean,
  companies: Company[],
  snackbarOpen: boolean,
  snackbarSeverity: 'success' | 'error' | 'warning' | 'info',
  snackbarMessage: string,
};

type Props = {
  open: boolean,
  companies: Company[],
  onClose: (createdUser?: User | null) => void,
};

class CreateUserModal extends Component<Props, State> {
  constructor(props: Props) {
    super(props);

    this.state = {
      newUser: {},
      companies: props.companies,
      loading: false,
      snackbarOpen: false,
      snackbarSeverity: 'success',
      snackbarMessage: '',
    };
  }

  async componentDidMount() {}

  componentDidUpdate(prevProps: Props) {
    if (prevProps.open !== this.props.open && !this.props.open) {
      this.setState({newUser: {}, loading: false});
    }
  }

  createUser = async () => {
    this.setState({loading: true});

    try {
      const createdUser = await createUser(this.state.newUser as any);
      this.setState({
        snackbarOpen: true,
        snackbarSeverity: 'success',
        snackbarMessage: 'User created successfully.',
      });
      this.props.onClose(createdUser);
    } catch (err) {
      console.error(err);
      this.setState({snackbarOpen: true, snackbarSeverity: 'error', snackbarMessage: 'An error occurred while creating the user.'});
    }

    this.setState({loading: false});
  };

  render() {
    const {open} = this.props;
    return <Modal open={open} closeAfterTransition={true} className={'create-user-modal-root'}>
      <Fade in={open} timeout={250}>
        <div className={'create-user-modal'}>
          <Paper className={'create-user-modal-paper'}>
            <IconButton
              size={'small'}
              className={'close-create-user-modal-button'}
              onClick={() => this.props.onClose(null)}
            >
              <Close />
            </IconButton>
            <Box className={'create-user-modal-content'}>
              <Box>
                <Grid container>
                  <Grid xs={12}><Typography variant={'h6'}>Creating User</Typography></Grid>
                </Grid>
              </Box>
              <Box width={'100%'} height={'calc(100% - 60px)'} overflow={'scroll'} pt={2} pb={2}>
                <Grid container width={'100%'} spacing={2}>
                  <Grid xs={12}>
                    <FormGroup>
                      <FormLabel>
                        First Name
                      </FormLabel>
                      <TextField
                        value={this.state.newUser.firstName || ''}
                        size={'small'}
                        disabled={this.state.loading}
                        onChange={event => this.setState({newUser: {...this.state.newUser, firstName: event.target.value}})}
                      />
                    </FormGroup>
                  </Grid>
                  <Grid xs={12}>
                    <FormGroup>
                      <FormLabel>
                        Last Name
                      </FormLabel>
                      <TextField
                        value={this.state.newUser.lastName || ''}
                        size={'small'}
                        disabled={this.state.loading}
                        onChange={event => this.setState({newUser: {...this.state.newUser, lastName: event.target.value}})}
                      />
                    </FormGroup>
                  </Grid>
                  <Grid xs={12}>
                    <FormGroup>
                      <FormLabel>
                        Email
                      </FormLabel>
                      <TextField
                        fullWidth
                        type={'email'}
                        value={this.state.newUser.email || ''}
                        size={'small'}
                        disabled={this.state.loading}
                        onChange={event => this.setState({newUser: {...this.state.newUser, email: event.target.value}})}
                      />
                    </FormGroup>
                  </Grid>
                  <Grid xs={12}>
                    <FormGroup>
                      <FormLabel>
                        Company
                      </FormLabel>
                      <Autocomplete
                        value={this.state.newUser.companyId ? this.props.companies.find(c => c.id === this.state.newUser.companyId) : null}
                        options={this.props.companies}
                        getOptionKey={(option) => option.id}
                        getOptionLabel={(option) => option.companyName || ''}
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                        size={'small'}
                        disabled={this.state.loading}
                        onChange={(_event, newValue) => {
                          if (newValue) {
                            this.setState({newUser: {...this.state.newUser, companyId: newValue.id}});
                          } else {
                            this.setState({newUser: {...this.state.newUser, companyId: undefined}});
                          }
                        }}
                        renderInput={(params) => <TextField {...params} />}
                      />
                    </FormGroup>
                  </Grid>
                </Grid>
              </Box>
              <Box height={'24px'}>
                <Grid container justifyContent={'flex-end'} spacing={1}>
                  <Grid xs={'auto'}>
                    <LoadingButton
                      loading={this.state.loading}
                      variant={'contained'}
                      color={'primary'}
                      loadingPosition={'start'}
                      startIcon={<SaveAs />}
                      sx={{color: 'white'}}
                      onClick={() => this.createUser()}
                      disabled={
                        !this.state.newUser.email
                        || !this.state.newUser.firstName
                        || !this.state.newUser.lastName
                        || !this.state.newUser.companyId
                      }
                    >
                      Create User
                    </LoadingButton>
                  </Grid>
                </Grid>
              </Box>
            </Box>
          </Paper>
          <Snackbar
            open={this.state.snackbarOpen}
            anchorOrigin={{vertical: 'bottom', horizontal: 'center'}}
            autoHideDuration={4000}
            onClose={(_event, reason) => reason !== 'clickaway' && this.setState({snackbarOpen: false})}
          >
            <Alert onClose={() => this.setState({snackbarOpen: false})} severity={this.state.snackbarSeverity} sx={{width: '100%'}}>
              {this.state.snackbarMessage}
            </Alert>
          </Snackbar>
        </div>
      </Fade>
    </Modal>;
  }
}

export default CreateUserModal;
